import { useState } from 'react'
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
} from '@mui/material'
import { DateTime } from 'luxon'
import { formatUrl } from '@utils'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import SelectedRowDetailsDialog from '@components/SelectedRowDetailsDialog'
import { Org } from '@types'
import dayjs from 'dayjs'
import API from '@enums/api'
import useDataSource from '@hooks/useDataSource'
import CustomToolbar from '@components/CustomToolbar'

const columns: GridColDef<Org>[] = [
    {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        width: 200,
        renderCell: (params: GridRenderCellParams<Org, string>) => (
            <div>
                <Link
                    href={
                        `/organizations/` + formatUrl(params.row.id as string)
                    }
                >
                    {params.value}
                </Link>
                <br />
                <span style={{ color: 'gray' }}>
                    {params.row.department || ''}
                </span>
            </div>
        ),
    },
    {
        field: 'created_date',
        headerName: 'Created Date',
        type: 'dateTime',
        width: 120,
        valueFormatter: (value) =>
            DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT),
    },
    {
        field: 'active',
        headerName: 'Active',
        width: 100,
        type: 'boolean',
        valueFormatter: (value) => (value === 1 ? 'Yes' : 'No'),
    },
    {
        field: 'limit_users',
        headerName: 'User Limit',
        width: 100,
        type: 'number',
        valueFormatter: (value) => (value < 0 ? '-' : value),
    },
    {
        field: 'address',
        headerName: 'Address',
        width: 250,
        type: 'string',
        filterable: false,
        sortable: false,
        valueGetter: (_value, row) => {
            const { address, city, state, country, postcode } = row
            return [address, city, state, country, postcode]
                .filter(Boolean)
                .join(', ')
        },
    },
    {
        field: 'timezone',
        headerName: 'Time Zone',
        type: 'string',
        width: 150,
    },
    {
        field: 'licence_initial_bill_date',
        headerName: 'Licence Initial Bill Date',
        type: 'dateTime',
        width: 200,
        valueFormatter: (value) =>
            value !== null ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
        field: 'team_id',
        headerName: 'Team ID',
        type: 'string',
        width: 200,
    },
    {
        field: 'totalUsers',
        headerName: 'Total Users',
        type: 'number',
        width: 150,
    },
    {
        field: 'totalActiveUsers',
        headerName: 'Total Active Users',
        type: 'number',
        width: 150,
    },
    {
        field: 'totalOrders',
        headerName: 'Total Orders',
        type: 'number',
        width: 150,
    },
    {
        field: 'totalOrdersLast30Days',
        headerName: 'Total Orders (last 30 days)',
        type: 'number',
        width: 150,
    },
]

const OrganizationsOverviewPage = () => {
    const [error, setError] = useState<string | null>(null)
    const [downloadError, setDownloadError] = useState<string | null>(null)
    const [selectedRow, setSelectedRow] = useState<Org | null>(null)

    const { dataSource, exporting, exportAllRows, setFilterModel } =
        useDataSource({
            getRows: async ({ paginationModel, sortModel, filterModel }) => {
                const res = await API.overviews.organizations({
                    paginationModel,
                    sortModel,
                    filterModel,
                    approval: 'approved',
                })

                if (!res.ok) {
                    console.error(res)
                    setError(res.error.message)
                    throw new Error(res.error.message)
                }

                return {
                    rows: res.value.rows,
                    rowCount: res.value.total_rows,
                }
            },
        })

    if (error) {
        return <DashboardErrorView error={error} />
    }

    return (
        <DashboardLayout>
            <Dialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
            >
                <DialogTitle>{downloadError}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sometimes this can be resolved by logging out and back
                        in again. If that does not work, please reach out to one
                        of the development team and we will investigate the
                        issue.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDownloadError(null)} autoFocus>
                        Dismiss
                    </Button>
                </DialogActions>
            </Dialog>
            <SelectedRowDetailsDialog
                onClose={() => setSelectedRow(null)}
                selectedRow={selectedRow}
            />
            <DataGridPro
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                dataSource={dataSource}
                onFilterModelChange={(m) => setFilterModel(m)}
                disableVirtualization // Disabled in order to prevent row-ghosting
                onRowClick={(params) => {
                    setSelectedRow(params.row)
                }}
                showToolbar
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        exporting,
                        exportAllRows,
                        exportFilename: 'organizations',
                    },
                }}
            />
        </DashboardLayout>
    )
}

export default OrganizationsOverviewPage
